import { createSelector } from '@reduxjs/toolkit';
import { WORKFLOW_MODE, RUN_STATUS, REPLAY_STEP_STATUS } from './constants';
import { AutomationStatusEnum } from '../../enums/AutomationStatusEnum';
import {
    findStepById,
    hasInstructionText,
    hasGeneratedSteps,
    getActiveFlatSteps,
    hasGenerationSessionArtifacts,
    mergeLeftPanelGroupSnapshotWithLiveSteps,
} from './planUtils';
import { validatePlanSteps } from './validationUtils';
import { initialState } from './automationStudioNewSlice';
import {
    resolveRecordingOverallStatus,
    resolveReplayOverallStatus,
} from './executionOutcomeUtils';
import { diffPlanAgainstBaseline, hasVisibleUnsavedChanges, getChangeTrackingBaselineInstructions } from './changeTrackingUtils';

const selectAutomationStudioNewState = (state) =>
    state.automationStudioNew ?? initialState;

export const selectWorkflowMode = (state) =>
    selectAutomationStudioNewState(state).workflow.mode;

export const selectWorkflowError = (state) =>
    selectAutomationStudioNewState(state).workflow.error;

export const selectPlanDraft = (state) =>
    selectAutomationStudioNewState(state).planDraft;

export const selectInstructions = (state) =>
    selectPlanDraft(state).instructions;

export const selectLeftPanelGroupSnapshotInstructions = (state) =>
    selectPlanDraft(state).leftPanelGroupSnapshotInstructions;

export const selectUsesLeftPanelGroupSnapshot = (state) =>
    Boolean(selectLeftPanelGroupSnapshotInstructions(state)?.length);

export const selectInstructionsForLeftPanel = createSelector(
    [selectInstructions, selectLeftPanelGroupSnapshotInstructions],
    (instructions, snapshot) => {
        if (!snapshot?.length) return instructions;
        return mergeLeftPanelGroupSnapshotWithLiveSteps(snapshot, instructions);
    }
);

export const selectDynamicResult = (state) =>
    selectPlanDraft(state).dynamicResult;

export const selectPlanIsDirty = (state) =>
    selectPlanDraft(state).isDirty;

export const selectInstructionsChangedSinceSteps = (state) =>
    Boolean(selectPlanDraft(state).instructionsChangedSinceSteps);

export const selectInstructionHeadingsSnapshot = (state) =>
    selectPlanDraft(state).instructionHeadingsSnapshot ?? {};

export const selectValidationErrorsByStepId = (state) =>
    selectPlanDraft(state).validationErrorsByStepId;

export const selectGenerationRun = (state) =>
    selectAutomationStudioNewState(state).generationRun;

export const selectReplayRun = (state) =>
    selectAutomationStudioNewState(state).replayRun;

export const selectSavedPlan = (state) =>
    selectAutomationStudioNewState(state).savedPlan;

export const selectSelection = (state) =>
    selectAutomationStudioNewState(state).selection;

export const selectSelectedStepId = (state) =>
    selectSelection(state).selectedStepId;

export const selectUi = (state) =>
    selectAutomationStudioNewState(state).ui;

export const selectRightPanelView = (state) =>
    selectUi(state).rightPanelView;

export const selectMcpToolsList = (state) =>
    selectAutomationStudioNewState(state).mcpToolsList;

export const selectMcpToolMap = (state) =>
    selectAutomationStudioNewState(state).mcpToolMap;

export const selectStepValidation = createSelector(
    [selectInstructions, selectMcpToolMap],
    (instructions, mcpToolMap) => validatePlanSteps(instructions, mcpToolMap)
);

export const selectIsStepValidationValid = (state) =>
    selectStepValidation(state).isValid;

export const selectIsGenerating = (state) =>
    selectGenerationRun(state).status === RUN_STATUS.RUNNING;

export const selectIsReplayExecuting = (state) =>
    selectReplayRun(state).status === RUN_STATUS.RUNNING;

export const selectInstructionsForExecutionView = createSelector(
    [selectInstructions, selectGenerationRun, selectReplayRun, selectIsReplayExecuting],
    (instructions, generationRun, replayRun, replayExecuting) => {
        if (replayExecuting || replayRun.status === RUN_STATUS.RUNNING) {
            return instructions;
        }

        if (
            replayRun.status === RUN_STATUS.COMPLETED &&
            Array.isArray(replayRun.instructionsSnapshot) &&
            replayRun.instructionsSnapshot.length > 0
        ) {
            return replayRun.instructionsSnapshot;
        }

        if (
            generationRun.status === RUN_STATUS.COMPLETED &&
            Array.isArray(generationRun.completedInstructions) &&
            generationRun.completedInstructions.length > 0
        ) {
            return generationRun.completedInstructions;
        }

        return instructions;
    }
);

export const selectFlatStepsForExecutionView = createSelector(
    [selectInstructionsForExecutionView],
    (instructions) => getActiveFlatSteps(instructions)
);

export const selectIsSaving = (state) =>
    selectWorkflowMode(state) === WORKFLOW_MODE.SAVING;

export const selectIsBusy = (state) =>
    selectIsGenerating(state) ||
    selectIsReplayExecuting(state) ||
    selectIsSaving(state);

export const selectSelectedStep = createSelector(
    [selectInstructions, selectSelectedStepId],
    (instructions, stepId) => findStepById(instructions, stepId)
);

export const selectVisibleInstructionsWithSteps = createSelector(
    [selectInstructions],
    (instructions) =>
        instructions.filter((inst) => !inst.removedFromEditor)
);

export const selectHasInstructions = createSelector(
    [selectInstructions],
    (instructions) => hasInstructionText(instructions)
);

export const selectHasAISteps = createSelector(
    [selectInstructions],
    (instructions) => hasGeneratedSteps(instructions)
);

export const selectHasSelectedTestCase = (state) =>
    Boolean(selectSavedPlan(state).testCaseId);

const selectChangeTrackingBaselineInstructions = createSelector(
    [selectSavedPlan, selectPlanDraft, selectGenerationRun],
    (savedPlan, planDraft, generationRun) =>
        getChangeTrackingBaselineInstructions({ savedPlan, planDraft, generationRun })
);

export const selectChangeTrackingDiff = createSelector(
    [
        selectInstructions,
        selectChangeTrackingBaselineInstructions,
        selectGenerationRun,
        selectReplayRun,
    ],
    (instructions, baseline, generationRun, replayRun) => {
        const empty = {
            dirtyInstructionIds: [],
            dirtyStepIds: [],
            fieldDirtyByStepId: {},
        };
        // Generate/replay are not user edits — only manual edits should show dirty chrome.
        const genBusy =
            generationRun?.status === RUN_STATUS.RUNNING ||
            generationRun?.status === RUN_STATUS.CONNECTING;
        const replayBusy =
            replayRun?.status === RUN_STATUS.RUNNING ||
            replayRun?.status === RUN_STATUS.CONNECTING;
        if (genBusy || replayBusy) return empty;
        if (!baseline) return empty;
        return diffPlanAgainstBaseline(instructions, baseline);
    }
);

export const selectHasUnsavedChanges = createSelector(
    [selectInstructions, selectChangeTrackingDiff],
    (instructions, diff) => hasVisibleUnsavedChanges(instructions, diff)
);

export const selectIsSavedExecutionRecord = (state) =>
    selectSavedPlan(state).isSaved &&
    !selectPlanIsDirty(state) &&
    !selectInstructionsChangedSinceSteps(state);

export const selectSaveChangeKind = createSelector(
    [
        selectInstructionsChangedSinceSteps,
        selectHasAISteps,
        selectPlanIsDirty,
        selectSavedPlan,
    ],
    (instructionsChangedSinceSteps, hasSteps, isDirty, savedPlan) => {
        if (instructionsChangedSinceSteps) return 'instructionsOnly';
        if (!hasSteps) return null;
        if (isDirty || !savedPlan.isSaved) return 'fullPlan';
        return null;
    }
);

export const selectCanReplay = createSelector(
    [
        selectHasAISteps,
        selectHasSelectedTestCase,
        selectIsBusy,
        selectInstructionsChangedSinceSteps,
        selectPlanIsDirty,
    ],
    (hasSteps, hasTestCase, isBusy, instructionsChangedSinceSteps, isDirty) =>
        hasSteps &&
        hasTestCase &&
        !isBusy &&
        !instructionsChangedSinceSteps &&
        !isDirty
);

export const selectCanGenerate = createSelector(
    [selectHasInstructions, selectIsBusy],
    (hasInstructions, isBusy) => hasInstructions && !isBusy
);

export const selectCanSave = createSelector(
    [
        selectSaveChangeKind,
        selectIsBusy,
        selectIsSavedExecutionRecord,
    ],
    (saveChangeKind, isBusy, isSavedExecutionRecord) => {
        if (isBusy || isSavedExecutionRecord) return false;
        return saveChangeKind !== null;
    }
);

export const selectStepFreshStatus = createSelector(
    [
        selectReplayRun,
        selectSavedPlan,
        selectPlanIsDirty,
        (_state, stepId) => stepId,
    ],
    (replayRun, savedPlan, isDirty, stepId) => {
        if (!stepId) return REPLAY_STEP_STATUS.PENDING;

        const replayStatus = replayRun.progressByStepId?.[stepId];
        if (replayRun.status === RUN_STATUS.RUNNING || replayRun.status === RUN_STATUS.COMPLETED) {
            return replayStatus ?? REPLAY_STEP_STATUS.PENDING;
        }

        if (savedPlan.isSaved && !isDirty) {
            return REPLAY_STEP_STATUS.PENDING;
        }

        return REPLAY_STEP_STATUS.PENDING;
    }
);

export const selectPlanForSave = createSelector(
    [selectInstructions, selectDynamicResult, selectSavedPlan],
    (instructions, dynamicResult, savedPlan) => ({
        instructions,
        dynamicResult,
        testCaseId: savedPlan.testCaseId,
        testCaseKey: savedPlan.testCaseKey,
    })
);

export const selectPlanForReplay = createSelector(
    [selectInstructions, selectDynamicResult, selectSavedPlan],
    (instructions, dynamicResult, savedPlan) => {
        if (!savedPlan.isSaved) return null;
        return {
            instructions,
            dynamicResult,
            testCaseId: savedPlan.testCaseId,
        };
    }
);

export const selectCanExport = (state, routeTestCaseId = null) => {
    const savedPlan = selectSavedPlan(state);
    const testCaseId = routeTestCaseId || savedPlan.testCaseId;
    const isNlpAutomated = savedPlan.automationStatus === AutomationStatusEnum.NLP_AUTOMATED;
    return (
        Boolean(testCaseId) &&
        isNlpAutomated &&
        !selectIsBusy(state) &&
        !savedPlan.isLoading
    );
};

export const selectIsNlpAutomatedTestCase = (state) =>
    selectSavedPlan(state).automationStatus === AutomationStatusEnum.NLP_AUTOMATED;

export const selectCanImport = createSelector(
    [selectHasInstructions, selectHasAISteps, selectIsBusy],
    (hasInstructions, hasSteps, isBusy) => !hasInstructions && !hasSteps && !isBusy
);

export const selectCanUploadFile = (state, projectId) =>
    Boolean(projectId) && !selectIsBusy(state);

export const selectReplayProgressByStepId = (state) =>
    selectReplayRun(state).progressByStepId ?? {};

export const selectReplayResultsByStepId = (state) =>
    selectReplayRun(state).resultsByStepId ?? {};

export const selectIsExecuting = (state) =>
    selectIsGenerating(state) || selectIsReplayExecuting(state);

export const selectActiveExecutionLogs = createSelector(
    [selectIsReplayExecuting, selectGenerationRun, selectReplayRun],
    (isReplay, generationRun, replayRun) =>
        isReplay ? (replayRun.logs ?? []) : (generationRun.logs ?? [])
);

/** Index-keyed progress map for legacy execution UI (flat step index → status). */
export const selectReplayProgressMap = createSelector(
    [selectReplayRun],
    (replayRun) => {
        const map = {};
        const indexToId = replayRun.stepIndexToStepId ?? {};
        Object.entries(indexToId).forEach(([idx, stepId]) => {
            const status = replayRun.progressByStepId?.[stepId];
            if (status != null) {
                map[Number(idx)] = status;
            }
        });
        return map;
    }
);

/** Index-keyed progress map for the current editable plan order, preserving results by step id. */
export const selectCurrentPlanReplayProgressMap = createSelector(
    [selectInstructions, selectReplayRun],
    (instructions, replayRun) => {
        const map = {};
        getActiveFlatSteps(instructions).forEach((step, index) => {
            const status = replayRun.progressByStepId?.[step.id];
            if (status != null) {
                map[index] = status;
            }
        });
        return map;
    }
);

/** Current replay flat step index derived from `currentStepId` in the current plan order. */
export const selectReplayCurrentStepIndex = createSelector(
    [selectReplayRun, selectInstructions],
    (replayRun, instructions) => {
        if (!replayRun.currentStepId) return null;
        const flatSteps = getActiveFlatSteps(instructions);
        const idx = flatSteps.findIndex((step) => step.id === replayRun.currentStepId);
        if (idx >= 0) return idx;

        const indexToId = replayRun.stepIndexToStepId ?? {};
        const entry = Object.entries(indexToId).find(([, id]) => id === replayRun.currentStepId);
        return entry ? Number(entry[0]) : null;
    }
);

function buildLegacyStepDetail(entry) {
    if (!entry) return null;
    const { stepIndex, result, screenshot, sessionId, status, tool, args, ...rest } = entry;
    const detail = {
        stepIndex,
        tool,
        args,
        sessionId,
        ...rest,
    };

    if (result != null) {
        detail.result = result;
    }
    if (screenshot != null) {
        detail.screenshot = screenshot;
    }

    if (status === REPLAY_STEP_STATUS.IN_PROGRESS || status === 'executing') {
        detail.status = 'executing';
    } else if (status === REPLAY_STEP_STATUS.PASSED) {
        detail.status = 'completed';
    } else if (status === REPLAY_STEP_STATUS.FAILED) {
        detail.status = 'failed';
    } else if (status === REPLAY_STEP_STATUS.SKIPPED) {
        detail.status = 'skipped';
    } else if (typeof status === 'string') {
        detail.status = status;
    }

    return detail;
}

/** Legacy `{ stepDetails, results, summary }` shape for execution / step editor UI. */
export const selectReplayExecResults = createSelector(
    [selectReplayRun, selectInstructions],
    (replayRun, instructions) => {
        const stepDetails = {};
        const results = [];

        getActiveFlatSteps(instructions).forEach((step, index) => {
            const entry = replayRun.resultsByStepId?.[step.id];
            const detail = buildLegacyStepDetail(entry);
            if (detail) {
                stepDetails[index] = detail;
                if (detail.result) {
                    results.push({
                        ...detail.result,
                        stepIndex: index,
                        screenshot: detail.screenshot,
                    });
                }
            }
        });

        const statusValues = Object.values(replayRun.progressByStepId ?? {});
        const passed = statusValues.filter((s) => s === REPLAY_STEP_STATUS.PASSED).length;
        const failed = statusValues.filter((s) => s === REPLAY_STEP_STATUS.FAILED).length;
        const total = statusValues.filter((s) =>
            [REPLAY_STEP_STATUS.PASSED, REPLAY_STEP_STATUS.FAILED, REPLAY_STEP_STATUS.SKIPPED].includes(s)
        ).length;

        return {
            stepDetails,
            results,
            summary:
                replayRun.summary ?? {
                    total,
                    passed,
                    failed,
                    successRate: total > 0 ? Math.round((passed / total) * 100) : 0,
                },
        };
    }
);

/** Overall pass/fail/partial summary for recording `testComplete` and user stop. */
export const selectGenerationOverallStatus = createSelector(
    [selectGenerationRun],
    (generationRun) => resolveRecordingOverallStatus(generationRun)
);

/** Overall pass/fail/partial summary for replay runs. */
export const selectReplayOverallStatus = createSelector(
    [selectReplayRun, selectReplayProgressMap],
    (replayRun, replayProgressMap) => resolveReplayOverallStatus(replayRun, replayProgressMap)
);

/** Execution totals / metadata from generation `testComplete` for results UI. */
export const selectGenerationExecutionResults = createSelector(
    [selectGenerationRun],
    (generationRun) => {
        const result = generationRun?.result;
        if (!result) return null;
        return {
            totalDuration:
                result.totalDuration ??
                (typeof result.executionTime === 'number' ? result.executionTime * 1000 : undefined),
            success: result.success,
        };
    }
);

export const selectDirtyInstructionIds = createSelector(
    [selectChangeTrackingDiff],
    (diff) => diff.dirtyInstructionIds
);

export const selectDirtyStepIds = createSelector(
    [selectChangeTrackingDiff],
    (diff) => diff.dirtyStepIds
);

export const selectSelectedStepFieldDirtyMap = createSelector(
    [selectSelectedStepId, selectChangeTrackingDiff],
    (stepId, diff) => {
        if (!stepId) return null;
        return diff.fieldDirtyByStepId[String(stepId)] ?? null;
    }
);
